import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService, LoginData, RegisteredCompanyOption } from '../services/auth.service';
import { useTranslation } from 'react-i18next';
import SetNewPasswordDialog from '../components/SetNewPasswordDialog';
import AuthShell from '../components/AuthShell';
import {
    ArrowRight,
    Database as DatabaseZap,
    Eye,
    EyeOff,
    Lock as LockKeyhole,
    Search,
    Tool as Factory,
    User as UserRound,
    X
} from 'react-feather';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

// Login is the first migrated shadcn-style surface and preserves existing auth flows.

export const Login: React.FC = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [selectedCompany, setSelectedCompany] = useState<RegisteredCompanyOption | null>(
        () => authService.getRememberedLoginCompany()
    );
    const [formData, setFormData] = useState<LoginData>({
        companyId: selectedCompany?.companyId,
        username: '',
        password: ''
    });
    const [companyQuery, setCompanyQuery] = useState(selectedCompany?.name || '');
    const [companyResults, setCompanyResults] = useState<RegisteredCompanyOption[]>([]);
    const [companySearchLoading, setCompanySearchLoading] = useState(false);
    const [companySearchError, setCompanySearchError] = useState('');
    const [companyResultsOpen, setCompanyResultsOpen] = useState(false);
    const companyInputRef = useRef<HTMLInputElement>(null);
    const [error, setError] = useState<string>('');
    const [passwordResetUser, setPasswordResetUser] = useState<any>(null);
    const [passwordResetToken, setPasswordResetToken] = useState<string | undefined>();
    const [showPasswordResetDialog, setShowPasswordResetDialog] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        try {
            if (sessionStorage.getItem('logoutReason') === 'SESSION_REVOKED') {
                setError(t('errorSessionRevoked'));
                sessionStorage.removeItem('logoutReason');
            }
        } catch {
            // Storage access is optional; login remains usable without it.
        }
    }, [t]);

    // Debounced public tenant lookup. Stale responses are ignored so quick typing
    // cannot replace newer results with an older request.
    useEffect(() => {
        const query = companyQuery.trim();
        if (selectedCompany || query.length < 2) {
            setCompanyResults([]);
            setCompanySearchLoading(false);
            setCompanySearchError('');
            return undefined;
        }

        let active = true;
        setCompanySearchLoading(true);
        const timer = window.setTimeout(async () => {
            setCompanySearchError('');
            try {
                const companies = await authService.searchRegisteredCompanies(query, 8);
                if (active) {
                    setCompanyResults(companies);
                    setCompanyResultsOpen(true);
                }
            } catch {
                if (active) {
                    setCompanyResults([]);
                    setCompanySearchError(t('companySearchFailed'));
                }
            } finally {
                if (active) {
                    setCompanySearchLoading(false);
                }
            }
        }, 300);

        return () => {
            active = false;
            window.clearTimeout(timer);
        };
    }, [companyQuery, selectedCompany, t]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const applyAuthenticatedLanguage = useCallback(async () => {
        try {
            await new Promise(resolve => setTimeout(resolve, 100));

            const dbLanguage = await authService.fetchLanguageFromDatabase();
            if (dbLanguage) {
                await i18n.changeLanguage(dbLanguage);
                localStorage.setItem('userLanguage', dbLanguage);
                window.dispatchEvent(new CustomEvent('authStateChanged', {
                    detail: { language: dbLanguage }
                }));
                return;
            }

            const savedLanguage = localStorage.getItem('userLanguage');
            if (savedLanguage === 'zh' || savedLanguage === 'en') {
                await i18n.changeLanguage(savedLanguage);
                window.dispatchEvent(new CustomEvent('authStateChanged', {
                    detail: { language: savedLanguage }
                }));
            }
        } catch (error) {
            console.error('[Login] Error fetching language setting:', error);
        }
    }, [i18n]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        try {
            const result = await authService.login({
                ...(selectedCompany ? { companyId: selectedCompany.companyId } : {}),
                username: formData.username,
                password: formData.password
            });

            // Check if password reset is required
            if (result.passwordResetRequired) {
                setPasswordResetUser(result.user || {
                    username: formData.username,
                    name: formData.username,
                    role: 'member'
                });
                setPasswordResetToken(result.passwordResetToken || result.resetToken);
                setShowPasswordResetDialog(true);
                return;
            }

            // Normal login success
            const decoded = authService.decodeToken();
            if (decoded) {
                await applyAuthenticatedLanguage();
                
                // Use window.location.href for full page reload to ensure language is applied
                // This forces the App.tsx to re-initialize with the correct language
                const targetPath = decoded.role === 'superadmin' ? '/superadmin/dashboard' : '/dashboard';
                console.log('[Login] 🔄 Redirecting to:', targetPath);
                window.location.href = targetPath;
            } else {
                setError(t('errorLoginFailedRole'));
                authService.logout();
            }
        } catch (err: any) {
            const errorMsg = err.response?.data?.error || 'errorGeneric';
            const errorCode = err.response?.data?.code;
            if (err.response?.status === 409 && errorCode === 'COMPANY_REQUIRED') {
                 setError(t('companyRequiredForDuplicateUsername'));
                 setCompanyResultsOpen(true);
                 companyInputRef.current?.focus();
            } else if (err.response?.status === 403 && errorMsg.includes('approved')) {
                 setError(t('errorLoginNotApproved'));
            } else if (err.response?.status === 403 && errorMsg.includes('expired')) {
                 setError(t('companyAccountExpired'));
            } else if (err.response?.status === 401) {
                 setError(t('errorLoginInvalid'));
            } else {
                setError(t(errorMsg, { defaultValue: t('errorGeneric') }));
            }
        }
    };

    const handlePasswordResetSuccess = async (token: string) => {
        // Password reset successful, token is stored by authService
        setShowPasswordResetDialog(false);
        setPasswordResetUser(null);
        setPasswordResetToken(undefined);
        await applyAuthenticatedLanguage();
        
        // Navigate to appropriate dashboard
        const decoded = authService.decodeToken();
        if (decoded) {
            navigate(decoded.role === 'superadmin' ? '/superadmin/dashboard' : '/dashboard');
        }
    };

    const handlePasswordResetError = (error: string) => {
        setError(error);
    };

    // 监听demo登录事件
    useEffect(() => {
        const handleDemoLogin = async (event: any) => {
            const { username, password } = event.detail;
            
            // 填入表单数据
            setFormData({ username, password });
            setError('');
            
            try {
                const response = await authService.login({
                    ...(username === 'superadmin' ? { companyId: 'M000000' } : {}),
                    username,
                    password
                });
                if (response.success) {
                    await applyAuthenticatedLanguage();
                    // 登录成功，跳转到dashboard
                    navigate('/dashboard');
                } else {
                    setError(response.message || t('loginError'));
                }
            } catch (err: any) {
                console.error('Demo login error:', err);
                setError(err.response?.data?.error || t('loginError'));
            }
        };

        window.addEventListener('demoLogin', handleDemoLogin);
        
        return () => {
            window.removeEventListener('demoLogin', handleDemoLogin);
        };
    }, [applyAuthenticatedLanguage, navigate, t]);

    return (
        <AuthShell
            action="register"
            contentClassName="mx-auto grid w-full max-w-7xl items-center gap-8 px-4 py-10 sm:px-6 lg:grid-cols-[minmax(0,1.1fr)_minmax(380px,0.7fr)] lg:px-8 lg:py-16"
            footer={(
                <footer className="login-footer border-t bg-card px-4 py-4 text-center text-sm text-muted-foreground">
                    {t('footerCopyright', { year: new Date().getFullYear() })}
                </footer>
            )}
            mainClassName="login-main surface-grid flex flex-1"
            onHelp={() => navigate('/help')}
            showLanguageToggle
        >
                    <section className="order-1 max-w-3xl">
                        <div className="login-eyebrow mb-5 inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-1 text-sm font-medium text-muted-foreground shadow-sm">
                            <Factory className="h-4 w-4 text-accent" aria-hidden="true" />
                            {t('landingEyebrow')}
                        </div>
                        <h1 className="max-w-3xl text-4xl font-semibold leading-tight text-foreground sm:text-5xl lg:text-6xl">
                            {t('landingHeader')}
                        </h1>
                        <p className="mt-5 max-w-2xl text-lg leading-8 text-muted-foreground">
                            {t('landingSubheader')}
                        </p>
                    </section>

                    <Card className="login-card order-2 border-border/80 shadow-soft lg:row-span-2">
                        <CardHeader className="space-y-2">
                            <CardTitle className="text-2xl">{t('loginTitle')}</CardTitle>
                            <CardDescription>{t('loginSubtitle')}</CardDescription>
                        </CardHeader>
                        <CardContent>
                            {error && (
                                <Alert variant="destructive" className="mb-4">
                                    <AlertDescription>{error}</AlertDescription>
                                </Alert>
                            )}
                            <form onSubmit={handleSubmit} noValidate className="space-y-5">
                                <div className="space-y-2">
                                        <Label htmlFor="company-search">{t('registeredCompanyOptionalLabel')}</Label>
                                        <div className="relative">
                                            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                                            <Input
                                                ref={companyInputRef}
                                                id="company-search"
                                                value={companyQuery}
                                                onChange={(event) => {
                                                    setCompanyQuery(event.target.value);
                                                    setSelectedCompany(null);
                                                    setFormData(previous => ({ ...previous, companyId: undefined }));
                                                    authService.clearRememberedLoginCompany();
                                                    setCompanyResultsOpen(true);
                                                }}
                                                onFocus={() => setCompanyResultsOpen(true)}
                                                onBlur={() => window.setTimeout(() => setCompanyResultsOpen(false), 120)}
                                                placeholder={t('registeredCompanyPlaceholder')}
                                                autoComplete="organization"
                                                role="combobox"
                                                aria-autocomplete="list"
                                                aria-expanded={companyResultsOpen && companyQuery.trim().length >= 2}
                                                aria-controls="company-search-results"
                                                className="pl-9 pr-9"
                                            />
                                            {(companyQuery || selectedCompany) && (
                                                <button
                                                    type="button"
                                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                                    aria-label={t('clearCompanySelection')}
                                                    onMouseDown={(event) => event.preventDefault()}
                                                    onClick={() => {
                                                        setCompanyQuery('');
                                                        setSelectedCompany(null);
                                                        setCompanyResults([]);
                                                        setFormData(previous => ({ ...previous, companyId: undefined }));
                                                        authService.clearRememberedLoginCompany();
                                                    }}
                                                >
                                                    <X className="h-4 w-4" aria-hidden="true" />
                                                </button>
                                            )}
                                            {companyResultsOpen && !selectedCompany && companyQuery.trim().length >= 2 && (
                                                <div
                                                    id="company-search-results"
                                                    role="listbox"
                                                    className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-md border bg-white p-1 text-slate-950 shadow-md"
                                                >
                                                    {companySearchLoading ? (
                                                        <p className="px-3 py-2 text-sm text-muted-foreground">{t('searchingCompanies')}</p>
                                                    ) : companyResults.length > 0 ? companyResults.map(company => (
                                                        <button
                                                            type="button"
                                                            key={company.companyId}
                                                            role="option"
                                                            aria-selected={false}
                                                            className="block w-full rounded bg-white px-3 py-2 text-left text-sm text-slate-950 hover:bg-slate-100"
                                                            onMouseDown={(event) => event.preventDefault()}
                                                            onClick={() => {
                                                                setSelectedCompany(company);
                                                                setCompanyQuery(company.name);
                                                                setFormData(previous => ({ ...previous, companyId: company.companyId }));
                                                                setCompanyResultsOpen(false);
                                                                authService.rememberLoginCompany(company);
                                                            }}
                                                        >
                                                            {company.name}
                                                        </button>
                                                    )) : (
                                                        <p className="px-3 py-2 text-sm text-muted-foreground">{t('noMatchingCompanies')}</p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        <p className={`text-sm ${companySearchError ? 'text-destructive' : 'text-muted-foreground'}`}>
                                            {companySearchError || (!companyQuery.trim()
                                                ? t('companyOptionalLoginHint')
                                                : companyQuery.trim().length < 2
                                                ? t('companySearchMinChars')
                                                : selectedCompany
                                                    ? t('companySelected', { companyName: selectedCompany.name })
                                                    : t('selectCompanyFromResults'))}
                                        </p>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="username">{t('usernameLabel')}</Label>
                                    <div className="relative">
                                        <UserRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                                        <Input
                                            required
                                            id="username"
                                            name="username"
                                            autoComplete="username"
                                            value={formData.username}
                                            onChange={handleChange}
                                            className="pl-9"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="password">{t('passwordLabel')}</Label>
                                    <div className="relative">
                                        <LockKeyhole className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                                        <Input
                                            name="password"
                                            type={showPassword ? 'text' : 'password'}
                                            id="password"
                                            autoComplete="current-password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            className="pl-9 pr-10"
                                        />
                                        <button
                                            type="button"
                                            className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                            onClick={() => setShowPassword(previous => !previous)}
                                            aria-label={t(showPassword ? 'hidePassword' : 'showPassword')}
                                            aria-pressed={showPassword}
                                        >
                                            {showPassword
                                                ? <EyeOff className="h-4 w-4" aria-hidden="true" />
                                                : <Eye className="h-4 w-4" aria-hidden="true" />}
                                        </button>
                                    </div>
                                    <p className="text-sm leading-6 text-muted-foreground">{t('passwordHintLogin')}</p>
                                </div>
                                <Button
                                    type="submit"
                                    size="lg"
                                    className="login-primary-action w-full"
                                    disabled={!formData.username.trim()}
                                >
                                    {t('signIn')}
                                    <ArrowRight className="h-4 w-4" aria-hidden="true" />
                                </Button>
                                <p className="text-center text-sm text-muted-foreground">
                                    {t('noAccount')}{' '}
                                    <button
                                        type="button"
                                        className="font-medium text-primary underline-offset-4 hover:underline"
                                        onClick={() => navigate('/register')}
                                    >
                                        {t('registerLink')}
                                    </button>
                                </p>
                            </form>
                        </CardContent>
                    </Card>

                    <div className="order-3 grid gap-4 sm:grid-cols-3 lg:self-start">
                        <div className="login-feature-card rounded-lg border bg-card p-4 shadow-sm">
                            <DatabaseZap className="mb-3 h-5 w-5 text-primary" aria-hidden="true" />
                            <p className="text-sm font-semibold text-foreground">{t('landingMetricKnowledge')}</p>
                            <p className="mt-1 text-sm leading-6 text-muted-foreground">{t('landingP1')}</p>
                        </div>
                        <div className="login-feature-card rounded-lg border bg-card p-4 shadow-sm">
                            <img src="/nx-logo.svg" alt="" className="mb-3 h-5 w-5" />
                            <p className="text-sm font-semibold text-foreground">{t('landingMetricNx')}</p>
                            <p className="mt-1 text-sm leading-6 text-muted-foreground">{t('landingP2')}</p>
                        </div>
                        <div className="login-feature-card rounded-lg border bg-card p-4 shadow-sm">
                            <LockKeyhole className="mb-3 h-5 w-5 text-teal-700" aria-hidden="true" />
                            <p className="text-sm font-semibold text-foreground">{t('landingMetricAccess')}</p>
                            <p className="mt-1 text-sm leading-6 text-muted-foreground">{t('landingAccessCopy')}</p>
                        </div>
                    </div>

            {showPasswordResetDialog && (
                <SetNewPasswordDialog
                    open={showPasswordResetDialog}
                    user={passwordResetUser}
                    passwordResetToken={passwordResetToken}
                    onSuccess={handlePasswordResetSuccess}
                    onError={handlePasswordResetError}
                />
            )}
        </AuthShell>
    );
}; 
